import React, { FunctionComponent, useState, useContext } from 'react';
import { useHistory } from 'react-router-dom';
import { makeStyles } from '@mui/styles';
import { Grid, Box, InputAdornment, Button, TextField } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import IconButton from '@mui/material/IconButton';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import { useForm, useFieldArray, Controller } from 'react-hook-form';
import SelectInput from '../../form/components/SelectInput';
import FilterSelectInput from '../../form/components/FilterSelectInput';
import DataTable from '../../dataTable/DataTable/DataTable';
import { IDataTableColumn } from '../../dataTable/DataTable/model';
import TextInput from '../../form/components/TextInput';
import { EstimateDetailsContext } from '../../estimateDetails/contexts/EstimateDetailsContext';
import NewInstallationCostModal from './NewInstallationCostModal';
import { EstimateCostsContext } from '../contexts/EstimateCostsContext';

const InstallationCosts = (props) => {
  const history = useHistory();
  const { salesEstimateId } = props;

  const [loading, setLoading] = React.useState(false);
  const [open, setOpen] = React.useState(false);
  const [laborOpen, setLaborOpen] = React.useState(false);
  const [subcontractorOpen, setSubcontractorOpen] = React.useState(false);
  const [miscOpen, setMiscOpen] = React.useState(false);
  const [laborRate, setLaborRate] = React.useState('Standard');

  const { installationCosts, deleteInstallationCostRecord } = React.useContext(EstimateCostsContext);

  const handleOpen = () => setOpen(true);
  const handleClose = () => setOpen(false);

  const handleSubcontractorOpen = () => setSubcontractorOpen(true);
  const handleSubcontractorClose = () => setSubcontractorOpen(false);

  const handleMiscOpen = () => setMiscOpen(true);
  const handleMiscClose = () => setMiscOpen(false);

  const handleLaborOpen = () => setLaborOpen(true);
  const handleLaborClose = () => setLaborOpen(false);

  const { control, handleSubmit, register, watch } = useForm({
    defaultValues: {
      name: '',
      assets: [],
    },
  });

  const { fields, append, remove } = useFieldArray({
    control,
    name: 'assets',
  });

  const onSubmit = (data) => {
    append(data);
    handleClose();
  };

  const handleEdit = (row) => {
    console.log('Edit clicked', row);
    // Add your edit logic here
  };

  const handleDelete = (row) => {
    console.log('row');
    console.log(row);
    deleteInstallationCostRecord(row.id);
  };

  const { salesEstimate } = React.useContext(EstimateDetailsContext);

  const nonLaborColumns = (): IDataTableColumn<any>[] => {
    return [
      {
        name: 'Asset',
        selector: 'asset_number',
        sortable: true,
        cell: (row) => <p>{row.asset_number}</p>,
      },
      {
        name: 'Description',
        selector: 'manufacturer',
        sortable: true,
        cell: (row) => <p>{row.name}</p>,
      },
      {
        name: 'Size',
        selector: 'cost_amount',
        sortable: true,
        cell: (row) => <p>{row.cost_amount}</p>,
      },
      {
        name: 'Cost',
        selector: 'total_cost',
        sortable: true,
        cell: (row) => <p>{row.total_cost}</p>,
      },
      {
        name: 'Actions',
        selector: 'total_cost',
        sortable: true,
        cell: (row) => (
          <div>
            <IconButton onClick={() => handleEdit(row)} aria-label="edit">
              <EditIcon />
            </IconButton>
            <IconButton onClick={() => handleDelete(row)} aria-label="delete">
              <DeleteIcon />
            </IconButton>
          </div>
        ),
      },
    ];
  };

  const laborColumns = (): IDataTableColumn<any>[] => {
    return [
      {
        name: 'Asset',
        selector: 'asset_number',
        sortable: true,
        cell: (row) => <p>{row.asset_number}</p>,
      },
      {
        name: 'Size',
        selector: 'asset_size',
        sortable: true,
        cell: (row) => <p>{row.asset_size}</p>,
      },
      {
        name: 'Rate',
        selector: 'rate',
        sortable: true,
        cell: (row) => <p>{row.rate}</p>,
      },
      {
        name: 'Hours',
        selector: 'hours',
        sortable: true,
        cell: (row) => <p>{row.hours}</p>,
      },
      {
        name: 'Level',
        selector: 'labor_level',
        sortable: true,
        cell: (row) => <p>{row.labor_level}</p>,
      },
      {
        name: 'Cost',
        selector: 'total_cost',
        sortable: true,
        cell: (row) => <p>{row.total_cost}</p>,
      },
      {
        name: 'Actions',
        selector: 'total_cost',
        sortable: true,
        cell: (row) => (
          <div>
            <IconButton onClick={() => handleEdit(row)} aria-label="edit">
              <EditIcon />
            </IconButton>
            <IconButton onClick={() => handleDelete(row)} aria-label="delete">
              <DeleteIcon />
            </IconButton>
          </div>
        ),
      },
    ];
  };

  const handleRowSelected = React.useCallback(
    (state) => {
      remove();
      const currentAssets = watch('assets');

      state.selectedRows?.forEach((siteAsset) => {
        if (!currentAssets.some((asset) => asset.asset === siteAsset.asset_number)) {
          append({
            manufacturer: siteAsset.manufacturer,
            asset: siteAsset.asset_number,
            model: siteAsset.model_number,
            serial: siteAsset.serial_number,
            age: siteAsset.vintage,
          });
        }
      });
    },
    [append, remove, watch],
  );

  const toggleCleared = () => {
    remove();
  };

  const rowSelectCritera = (row) => {
    return watch('assets').some((asset) => asset.asset === row.asset_number && asset.model === row.model_number);
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <br />
      <h6 style={{ marginBottom: 8 }}>Material</h6>
      <DataTable
        title="Assets"
        columns={nonLaborColumns()}
        data={salesEstimate.sales_estimate_assets?.flatMap((asset) => asset.material_costs)}
        striped
        dense
        highlightOnHover
        pointerOnHover
        noHeader
        onSelectedRowsChange={handleRowSelected}
        clearSelectedRows={toggleCleared}
        selectableRowSelected={rowSelectCritera}
      />
      <br />
      <NewInstallationCostModal
        title="Material"
        open={open}
        handleClose={handleClose}
        salesEstimate={salesEstimate}
        descriptionOptions={installationCosts?.filter((option: any) => option.primary_category === 'material')}
      />
      <Button variant="contained" onClick={handleOpen} type="button">
        Add Material
      </Button>
      <br />
      <hr />
      <br />
      <Grid container spacing={3}>
        <Grid item>
          <h6 style={{ marginBottom: 8 }}>Labor</h6>
        </Grid>
        <Grid item>
          <FilterSelectInput
            label="Rate"
            value={laborRate}
            valueChanged={(value) => setLaborRate(value)}
            choices={[
              { label: 'Standard', value: 'Standard' },
              { label: 'Overtime/Weekend', value: 'Overtime/Weekend' },
            ]}
          />
        </Grid>
      </Grid>
      <DataTable
        title="Assets"
        columns={laborColumns()}
        data={salesEstimate.sales_estimate_assets?.flatMap((asset) => asset.labor_costs)}
        striped
        dense
        highlightOnHover
        pointerOnHover
        noHeader
        onSelectedRowsChange={handleRowSelected}
        clearSelectedRows={toggleCleared}
        selectableRowSelected={rowSelectCritera}
      />
      <br />
      <NewInstallationCostModal
        title="Labor"
        open={laborOpen}
        handleClose={handleLaborClose}
        salesEstimate={salesEstimate}
        descriptionOptions={installationCosts?.filter((option: any) => option.primary_category === 'labor')}
      />
      <Button variant="contained" onClick={handleLaborOpen} type="button">
        Add Labor
      </Button>
      <br />
      <hr />
      <br />
      <h6 style={{ marginBottom: 8 }}>Subcontractor</h6>
      <DataTable
        title="Assets"
        columns={nonLaborColumns()}
        data={salesEstimate.sales_estimate_assets?.flatMap((asset) => asset.subcontractor_costs)}
        striped
        dense
        highlightOnHover
        pointerOnHover
        noHeader
        onSelectedRowsChange={handleRowSelected}
        clearSelectedRows={toggleCleared}
        selectableRowSelected={rowSelectCritera}
      />
      <br />
      <NewInstallationCostModal
        title="Subcontractor"
        open={subcontractorOpen}
        handleClose={handleSubcontractorClose}
        salesEstimate={salesEstimate}
        descriptionOptions={installationCosts?.filter((option: any) => option.primary_category === 'subcontractor')}
      />
      <Button variant="contained" onClick={handleSubcontractorOpen} type="button">
        Add Sub
      </Button>
      <br />
      <hr />
      <br />
      <h6 style={{ marginBottom: 8 }}>Miscellaneous</h6>
      <DataTable
        title="Assets"
        columns={nonLaborColumns()}
        data={salesEstimate.sales_estimate_assets?.flatMap((asset) => asset.misc_costs)}
        striped
        dense
        highlightOnHover
        pointerOnHover
        noHeader
        onSelectedRowsChange={handleRowSelected}
        clearSelectedRows={toggleCleared}
        selectableRowSelected={rowSelectCritera}
      />
      <br />
      <NewInstallationCostModal
        title="Misc"
        open={miscOpen}
        handleClose={handleMiscClose}
        salesEstimate={salesEstimate}
        descriptionOptions={installationCosts?.filter((option: any) => option.primary_category === 'miscellaneous')}
      />
      <Button variant="contained" onClick={handleMiscOpen} type="button">
        Add Misc
      </Button>
      <br />
      <hr />
      <br />
      <Button
        variant="contained"
        color="primary"
        onClick={() => history.push(`/rooftop_automation/${salesEstimateId}/rebates`)}
      >
        Submit Installation
      </Button>
    </form>
  );
};

export default InstallationCosts;
